import React from 'react';
import { X, AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';

export type BannerType = 'info' | 'warning' | 'success' | 'error';

interface ActionBannerProps {
    type?: BannerType;
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
    onDismiss?: () => void;
    dismissible?: boolean;
}

const bannerStyles = {
    info: {
        container: 'bg-primaryGreen/10 border-primaryGreen/20 text-gray-900',
        icon: 'text-primaryGreen',
        button: 'bg-primaryGreen hover:bg-green-700 text-white',
        Icon: Info,
    },
    warning: {
        container: 'bg-yellow-50 border-yellow-200 text-yellow-900',
        icon: 'text-yellow-600',
        button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
        Icon: AlertTriangle,
    },
    success: {
        container: 'bg-green-50 border-green-200 text-green-900',
        icon: 'text-green-600',
        button: 'bg-green-600 hover:bg-green-700 text-white',
        Icon: CheckCircle,
    },
    error: {
        container: 'bg-red-50 border-red-200 text-red-900',
        icon: 'text-red-600',
        button: 'bg-red-600 hover:bg-red-700 text-white',
        Icon: AlertCircle,
    },
};

export const ActionBanner: React.FC<ActionBannerProps> = ({
    type = 'info',
    title,
    message,
    actionLabel,
    onAction,
    onDismiss,
    dismissible = true,
}) => {
    const styles = bannerStyles[type];
    const IconComponent = styles.Icon;

    return (
        <div className={`relative border-2 rounded-lg p-4 ${ styles.container } shadow-sm`}>
            <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 ${ styles.icon }`}>
                    <IconComponent className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold mb-1">{title}</h3>
                    <p className="text-sm opacity-90">{message}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {actionLabel && onAction && (
                        <button
                            onClick={onAction}
                            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${ styles.button }`}
                        >
                            {actionLabel}
                        </button>
                    )}
                    {dismissible && onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="p-1 hover:bg-black/5 rounded transition-colors"
                            aria-label="Dismiss"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
